
import React, { useState, useEffect } from 'react';
import { Folder, Plus, RefreshCw, Search, Briefcase, Database, AlertCircle, Loader2 } from 'lucide-react';
import { AppSettings, DBProject, ProjectState } from '../types';

interface ProjectSidebarProps {
  isOpen: boolean;
  settings: AppSettings;
  currentProjectId: string;
  onSelectProject: (id: string) => void;
  onRefresh: () => void;
}

const ProjectSidebar: React.FC<ProjectSidebarProps> = ({ isOpen, settings, currentProjectId, onSelectProject, onRefresh }) => {
  const [projects, setProjects] = useState<DBProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch projects on mount or when settings change
  useEffect(() => {
    if (settings.useBackend) {
      fetchProjects();
    }
  }, [settings.useBackend, settings.backendUrl, settings.dbConfig]);

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${settings.backendUrl}/api/framework/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dbConfig: settings.dbConfig })
      });
      const data = await response.json();
      if (data.success) {
        setProjects(data.projects);
      } else {
        // Silent fail or minimal error for sidebar
        console.error(data.error);
        setError('Failed to load');
      }
    } catch (err: any) {
      console.error(err);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isOpen) return null;

  return (
    <div className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col h-full shrink-0 transition-all duration-300">
      {/* Header */}
      <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
        <div className="flex items-center gap-2 text-slate-200 font-semibold">
          <Briefcase className="w-5 h-5 text-indigo-500" />
          <span>Projects</span>
        </div>
        <button 
          onClick={fetchProjects} 
          disabled={loading}
          className="p-1.5 text-slate-500 hover:text-white rounded-md hover:bg-slate-800 transition-colors"
          title="Refresh List"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Search */}
      <div className="p-3 border-b border-slate-800">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
          <input 
            type="text" 
            placeholder="Filter projects..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-indigo-500/50"
          />
        </div>
      </div>

      {/* Project List */}
      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar space-y-1">
        {!settings.useBackend ? (
           <div className="p-4 text-center text-slate-500 text-xs">
              <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Connect to Remote Backend to view database projects.</p>
           </div>
        ) : error ? (
            <div className="p-4 text-center text-red-400 text-xs">
                <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                <p>Could not fetch projects.</p>
                <button onClick={fetchProjects} className="mt-2 text-indigo-400 hover:underline">Retry</button>
            </div>
        ) : (
            <>
                {filteredProjects.map(p => (
                <button
                    key={p.id}
                    onClick={() => onSelectProject(p.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-all group ${
                    currentProjectId === p.id 
                        ? 'bg-indigo-900/20 border-indigo-500/50 shadow-sm' 
                        : 'bg-transparent border-transparent hover:bg-slate-800 hover:border-slate-700'
                    }`}
                >
                    <div className="flex justify-between items-start mb-1">
                    <span className={`text-sm font-medium truncate ${currentProjectId === p.id ? 'text-indigo-300' : 'text-slate-300 group-hover:text-white'}`}>
                        {p.name}
                    </span>
                    {p.lifecycle_stage && (
                        <span className="text-[9px] uppercase tracking-wider bg-slate-950 px-1.5 py-0.5 rounded text-slate-500 border border-slate-800">
                        {p.lifecycle_stage}
                        </span>
                    )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{p.description || "No description"}</p>
                    <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-600 font-mono">
                        <Folder className="w-3 h-3" />
                        <span>{p.id}</span>
                    </div>
                </button>
                ))}
                
                {filteredProjects.length === 0 && !loading && (
                    <div className="p-4 text-center text-slate-500 text-xs italic">
                        No projects found.
                    </div>
                )}
            </>
        )}
      </div>

      {/* Footer / Add Project Placeholder */}
      <div className="p-3 border-t border-slate-800 bg-slate-900/50">
        <button 
            className="w-full py-2 border border-dashed border-slate-700 rounded-lg text-slate-500 text-xs hover:border-slate-500 hover:text-slate-300 transition-colors flex items-center justify-center gap-2 cursor-help"
            title="Create Project via Database for now"
        >
            <Plus className="w-3 h-3" /> Add New Project
        </button>
      </div>
    </div>
  );
};

export default ProjectSidebar;
